import { useState, useRef, useEffect } from "react";
import { Textarea } from "@/shared/components/ui/textarea";
import { Button } from "@/shared/components/ui/button";
import { Calendar, ChevronDown } from "lucide-react";
import toast from "react-hot-toast";
import { useApi } from "@/shared/utils/request";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import Cal, { getCalApi } from "@calcom/embed-react";
import styles from "./SupportView.module.css";

type IssueType = "bug" | "feature" | "question" | null;

interface FormData {
  // Bug fields
  whatHappened: string;
  expectedBehavior: string;
  bugAnythingElse: string;
  // Feature fields
  featureDescription: string;
  currentWorkaround: string;
  featureAnythingElse: string;
  // Question field
  question: string;
}

interface UploadedImage {
  file: File;
  preview: string;
}

const initialFormData: FormData = {
  whatHappened: "",
  expectedBehavior: "",
  bugAnythingElse: "",
  featureDescription: "",
  currentWorkaround: "",
  featureAnythingElse: "",
  question: "",
};

const MAX_SCREENSHOTS = 3;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const ALLOWED_EXTENSIONS = ["PNG", "JPG", "JPEG", "GIF", "WEBP"];

// Convert File to base64 data URL
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function SupportView() {
  const [issueType, setIssueType] = useState<IssueType>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { makeApiRequest } = useApi();
  const { darkMode } = useDarkMode();

  // Initialize Cal.com embed API when calendar is expanded or theme changes
  useEffect(() => {
    if (!calendarExpanded) return;
    (async function () {
      const cal = await getCalApi({ namespace: "hourglass-1-1-support-call" });
      cal("ui", {
        theme: darkMode ? "dark" : "light",
        cssVarsPerTheme: {
          light: { "cal-brand": "#1a1a1a" },
          dark: {
            "cal-brand": "#fafafa",
            "cal-bg": "#282c34",
            "cal-bg-emphasis": "#21252b",
          },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
    })();
  }, [calendarExpanded, darkMode]);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `"${file.name}" is not a supported image format. Please use ${ALLOWED_EXTENSIONS.join(", ")}.`,
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return {
        valid: false,
        error: `"${file.name}" is too large (${fileSizeMB}MB). Maximum file size is ${MAX_FILE_SIZE_MB}MB.`,
      };
    }

    return { valid: true };
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const remainingSlots = MAX_SCREENSHOTS - uploadedImages.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum of ${MAX_SCREENSHOTS} screenshots allowed.`);
      return;
    }

    const newImages: UploadedImage[] = [];
    const errors: string[] = [];
    const filesToProcess = Math.min(files.length, remainingSlots);
    let skippedDueToSlots = files.length > remainingSlots ? files.length - remainingSlots : 0;

    // Helper to check if file is duplicate (same name + size)
    const isDuplicate = (file: File): boolean => {
      const existsInUploaded = uploadedImages.some(
        (img) => img.file.name === file.name && img.file.size === file.size
      );
      const existsInNew = newImages.some(
        (img) => img.file.name === file.name && img.file.size === file.size
      );
      return existsInUploaded || existsInNew;
    };

    for (let i = 0; i < filesToProcess; i++) {
      const file = files[i];

      // Check for duplicates first
      if (isDuplicate(file)) {
        errors.push(`"${file.name}" has already been added.`);
        continue;
      }

      const validation = validateFile(file);

      if (validation.valid) {
        newImages.push({
          file,
          preview: URL.createObjectURL(file),
        });
      } else if (validation.error) {
        errors.push(validation.error);
      }
    }

    // Show errors for invalid files
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
    }

    // Show warning if some files were skipped due to slot limit
    if (skippedDueToSlots > 0) {
      toast.error(
        `${skippedDueToSlots} file${skippedDueToSlots > 1 ? "s were" : " was"} not added. Maximum of ${MAX_SCREENSHOTS} screenshots allowed.`
      );
    }

    // Add valid images
    if (newImages.length > 0) {
      setUploadedImages((prev) => [...prev, ...newImages]);
      if (errors.length === 0 && skippedDueToSlots === 0) {
        toast.success(
          `${newImages.length} screenshot${newImages.length > 1 ? "s" : ""} added.`
        );
      }
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages((prev) => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const resetForm = () => {
    setIssueType(null);
    setFormData(initialFormData);
    // Clean up image previews
    uploadedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setUploadedImages([]);
  };

  const handleSubmit = async () => {
    if (!issueType || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Convert all images to base64
      const screenshots = await Promise.all(
        uploadedImages.map((img) => fileToBase64(img.file))
      );

      // Build request body
      const body = {
        issueType,
        ...formData,
        screenshots,
      };

      const response = await makeApiRequest({
        endpoint: "/feedback",
        method: "POST",
        body,
      });

      if (response.error) {
        throw new Error(String(response.error));
      }

      toast.success("Thank you! Your feedback has been submitted.");
      resetForm();
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    if (!issueType) return false;
    switch (issueType) {
      case "bug":
        return formData.whatHappened.trim().length > 0 && formData.expectedBehavior.trim().length > 0;
      case "feature":
        return formData.featureDescription.trim().length > 0;
      case "question":
        return formData.question.trim().length > 0;
      default:
        return false;
    }
  };

  const canAddMoreImages = uploadedImages.length < MAX_SCREENSHOTS;

  return (
    <div className={styles.supportViewWrapper}>
      <div className={styles.supportViewContainer}>
        {/* Header with Schedule Button */}
        <div className={styles.supportHeader}>
          <div className={styles.supportHeaderLeft}>
            <h2 className={styles.supportTitle}>Support</h2>
            <p className={styles.supportSubtitle}>
              Have a question, found a bug, or want to request a feature? Let us know below or schedule a support meeting.
            </p>
          </div>
          <button
            className={styles.scheduleButton}
            onClick={() => setCalendarExpanded(!calendarExpanded)}
          >
            <Calendar size={18} />
            <span>Schedule a Meeting</span>
            <ChevronDown
              size={16}
              className={`${styles.scheduleChevron} ${calendarExpanded ? styles.scheduleChevronOpen : ""}`}
            />
          </button>
        </div>

        {/* Collapsible Cal.com Scheduling */}
        {calendarExpanded && (
          <div className={`${styles.calendarContainer} ${darkMode ? styles.calendarContainerDark : ""}`}>
            <Cal
              namespace="hourglass-1-1-support-call"
              calLink="team/hourglass/hourglass-1-1-support-call"
              style={{ width: "100%", height: "100%", overflow: "hidden" }}
              config={{ layout: "month_view", theme: darkMode ? "dark" : "light" }}
            />
          </div>
        )}

        {/* Issue Type Selector */}
        <div className={styles.supportTypeSelector}>
          <p className={styles.supportLabel}>What type of issue is this?</p>
          <div className={styles.supportTypeCards}>
            <button
              className={`${styles.supportTypeCard} ${issueType === "bug" ? styles.supportTypeCardSelected : ""}`}
              onClick={() => setIssueType("bug")}
            >
              <span className={styles.supportTypeLabel}>Bug Report</span>
              <span className={styles.supportTypeDescription}>Something isn't working correctly</span>
            </button>
            <button
              className={`${styles.supportTypeCard} ${issueType === "feature" ? styles.supportTypeCardSelected : ""}`}
              onClick={() => setIssueType("feature")}
            >
              <span className={styles.supportTypeLabel}>Feature Request</span>
              <span className={styles.supportTypeDescription}>Suggest an improvement or new capability</span>
            </button>
            <button
              className={`${styles.supportTypeCard} ${issueType === "question" ? styles.supportTypeCardSelected : ""}`}
              onClick={() => setIssueType("question")}
            >
              <span className={styles.supportTypeLabel}>Question</span>
              <span className={styles.supportTypeDescription}>Get help with using Hourglass</span>
            </button>
          </div>
        </div>

        {/* Conditional Form Fields */}
        {issueType && (
          <div className={styles.supportFormFields}>
            {/* Bug Report Fields */}
            {issueType === "bug" && (
              <>
                <div className={styles.supportFieldGroup}>
                  <label className={styles.supportLabel}>
                    What happened? <span className={styles.supportRequired}>*</span>
                  </label>
                  <Textarea
                    value={formData.whatHappened}
                    onChange={(e) => updateField("whatHappened", e.target.value)}
                    placeholder="Describe what went wrong.

For example: When I clicked Export, nothing happened and my entries didn't appear in ProLaw."
                    className="min-h-[120px] resize-y"
                  />
                </div>
                <div className={styles.supportFieldGroup}>
                  <label className={styles.supportLabel}>
                    What should have happened? <span className={styles.supportRequired}>*</span>
                  </label>
                  <Textarea
                    value={formData.expectedBehavior}
                    onChange={(e) => updateField("expectedBehavior", e.target.value)}
                    placeholder="Describe what you were trying to accomplish.

For example: My 5 time entries should have synced to ProLaw and appeared in my timesheet."
                    className="min-h-[120px] resize-y"
                  />
                </div>
                <div className={styles.supportFieldGroup}>
                  <label className={styles.supportLabel}>Anything else we should know?</label>
                  <Textarea
                    value={formData.bugAnythingElse}
                    onChange={(e) => updateField("bugAnythingElse", e.target.value)}
                    placeholder="Any additional context, error messages, or details that might help us investigate."
                    className={styles.supportTextarea}
                  />
                </div>
              </>
            )}

            {/* Feature Request Fields */}
            {issueType === "feature" && (
              <>
                <div className={styles.supportFieldGroup}>
                  <label className={styles.supportLabel}>
                    What feature would help you? <span className={styles.supportRequired}>*</span>
                  </label>
                  <Textarea
                    value={formData.featureDescription}
                    onChange={(e) => updateField("featureDescription", e.target.value)}
                    placeholder="Describe the feature you'd like.

For example: A way to bulk-edit multiple time entries at once."
                    className={styles.supportTextarea}
                  />
                </div>
                <div className={styles.supportFieldGroup}>
                  <label className={styles.supportLabel}>How would this help your workflow?</label>
                  <Textarea
                    value={formData.currentWorkaround}
                    onChange={(e) => updateField("currentWorkaround", e.target.value)}
                    placeholder="For example: I often need to change the matter on 10+ entries and doing them one-by-one takes too long."
                    className={styles.supportTextarea}
                  />
                </div>
                <div className={styles.supportFieldGroup}>
                  <label className={styles.supportLabel}>Anything else we should know?</label>
                  <Textarea
                    value={formData.featureAnythingElse}
                    onChange={(e) => updateField("featureAnythingElse", e.target.value)}
                    placeholder="Any additional context about how this impacts your work or ideas you have for a solution."
                    className={styles.supportTextarea}
                  />
                </div>
              </>
            )}

            {/* Question Fields */}
            {issueType === "question" && (
              <div className={styles.supportFieldGroup}>
                <label className={styles.supportLabel}>
                  What's your question? <span className={styles.supportRequired}>*</span>
                </label>
                <Textarea
                  value={formData.question}
                  onChange={(e) => updateField("question", e.target.value)}
                  placeholder="For example: How do I change my default task code?"
                  className={styles.supportTextarea}
                />
              </div>
            )}

            {/* Screenshot Upload - Always shown when type selected */}
            <div className={styles.supportFieldGroup}>
              <label className={styles.supportLabel}>
                Add screenshots (optional) — {uploadedImages.length} of {MAX_SCREENSHOTS}
              </label>

              {/* Uploaded Images Preview */}
              {uploadedImages.length > 0 && (
                <div className={styles.supportImagePreviewContainer}>
                  {uploadedImages.map((image, index) => (
                    <div key={index} className={styles.supportImagePreview}>
                      <img src={image.preview} alt={`Screenshot ${index + 1}`} />
                      <button
                        className={styles.supportImageRemove}
                        onClick={() => removeImage(index)}
                        type="button"
                        aria-label="Remove screenshot"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Zone */}
              {canAddMoreImages && (
                <div
                  className={`${styles.supportUploadZone} ${isDragging ? styles.supportUploadZoneDragging : ""}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_IMAGE_TYPES.join(",")}
                    multiple
                    onChange={handleFileInputChange}
                    className={styles.supportFileInput}
                  />
                  <div className={styles.supportUploadContent}>
                    <span className={styles.supportUploadText}>
                      Drag and drop images here, or click to browse
                    </span>
                  </div>
                </div>
              )}
              <p className={styles.supportUploadHint}>
                Accepted formats: {ALLOWED_EXTENSIONS.join(", ")}. Maximum size: {MAX_FILE_SIZE_MB}MB per file.
              </p>
            </div>

            {/* Submit Button */}
            <div className={styles.supportSubmitRow}>
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid() || isSubmitting}
                className={styles.supportSubmitButton}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
